/* Keeps track of stack of urls the user navigates to.
   Via the React Router's history.listen callback, this stack reacts to URL pop, push en replace events.
   This stack needs to be manually maintained, because React Router's history object only holds the current URL.
   To be able to know what the previous URL is (e.g. to be able to show 'Back to Home' or 'Back to Roster' we keep track of this stack here.

   ⚠️ Do not use history.goBack but use the goBack() method on this object, otherwise unexpected behaviour can occur!


   */

import debounce from './debounce';
import { sendPageview } from './analytics';

export default class NavigationHistory {
  constructor(history, linkUrlForFn, navigateFn) {
    this.history = history;
    this.urls = [this.history.location]; // Add the current location as first entry

    this.installHistoryListener();
    this.linkUrlFor = linkUrlForFn;
    this.navigate = navigateFn;

    this.navigationFlag = false;
    this.previousURL = false;

    this.widgets = [];
    this.popoverWidgets = [];
  }

  setWidgets(widgets) {
    this.widgets = widgets.map((widget) => ({
      ...widget,
      linkTo: { pathname: this.linkUrlFor(widget.linkTo) }
    }));
  }

  setPopoverWidgets(popoverWidgets) {
    this.popoverWidgets = popoverWidgets.map((popoverWidget) => ({
      ...popoverWidget,
      linkTo: { pathname: this.linkUrlFor(`/${popoverWidget.id}`) }
    }));
  }

  /* History and navigation methods */
  size() {
    return this.urls.length;
  }

  isEmpty() {
    return this.size() === 0;
  }

  indexLastUrl() {
    return this.size() - 1;
  }

  push(url) {
    if (!this.navigationFlag) this.navigationFlag = true;
    if (!this.isEmpty()) this.previousURL = this.peek();
    this.urls.push(url);
  }

  replace(url) {
    if (!this.isEmpty()) this.previousURL = this.peek();
    this.urls[this.indexLastUrl()] = url;
  }

  pop() {
    if (this.isEmpty()) return false;
    return this.urls.splice(this.indexLastUrl())[0];
  }

  peekAt(index) {
    if (this.isEmpty()) return false;
    return this.urls[this.indexLastUrl() - index];
  }

  peek() {
    if (this.isEmpty()) return false;
    return this.urls[this.indexLastUrl()];
  }

  hasNavigated() {
    return this.navigationFlag;
  }

  installHistoryListener() {
    this.history.listen(({ action, location }) => {
      if (action === 'PUSH') this.push(location);
      else if (action === 'REPLACE') this.replace(location);
      else if (action === 'POP') this.pop();
    });
  }

  resetAfterLogin() {
    const lastVisited = this.peek();
    this.urls = [];
    this.push(lastVisited);
  }

  /* Navigation */

  /* This function needs to be used for navigating back from a certain url.
     It makes sure that if this object's stack of urls is empty, we go back to the home page.
     This is not always the case for the history.goBack() because that stack is not always reset */
  goBack(options = {}) {
    const previousURL = this.pop();
    if (this.isEmpty()) {
      this.triggerSendPageview('');
      this.navigate(this.linkUrlFor(''));
    } else {
      const top = this.peek();
      const completeUrl = top.pathname + (top.hash || '');
      this.triggerSendPageview(completeUrl);
      this.navigate(completeUrl, { ...options, replace: true });
      this.previousURL = previousURL;
    }
  }

  goTo(url, replace = false, options = {}, sendPageviewOnReplace = false) {
    if (replace) {
      this.navigate(url, { replace: true, ...options });
      if (sendPageviewOnReplace) {
        this.triggerSendPageview(url);
      }
    } else {
      this.triggerSendPageview(url);
      this.navigate(url, options);
    }
  }

  debouncedGoTo = debounce(this.goTo);

  replaceUrl(url, options = {}) {
    this.navigate(url, { ...options, replace: true });
  }

  triggerSendPageview(url) {
    let urlToSendToPageview = url;
    /* When passing on state parameters url could be object */
    if (typeof url === 'object') {
      const completeUrl = url.pathname + (url.hash || '');
      urlToSendToPageview = completeUrl;
    }

    sendPageview(urlToSendToPageview);
  }

  /* Helper functions */

  isLatestActionPushUrl() {
    return this.history.action === 'PUSH';
  }

  isSameAsPreviousURL(url) {
    return (
      this.previousURL.pathname === url.pathname && this.previousURL.hash === url.hash
    );
  }

  isValidURL(url) {
    return this.isHomepageURL(url) || this.isWidgetURL(url) || this.isPopoverURL(url);
  }

  willNavigateBackHome() {
    const previousURL = this.peekAt(1);
    return (
      this.size() === 1 ||
      (previousURL &&
        (this.isHomepageURL(previousURL.pathname) ||
          this.isPopoverURL(previousURL.pathname))) ||
      (previousURL && !this.isValidURL(previousURL.pathname))
    );
  }

  getPreviousWidgetName(shortenedName = false) {
    const previousURL = this.peekAt(1);
    if (!previousURL) return '';
    return shortenedName
      ? this.getAppShortenedWidgetNameForPath(previousURL.pathname)
      : this.getAppNameForPath(previousURL.pathname);
  }

  getTextForBackButton(shortenedName = true) {
    const previousPath = this.peekAt(1);
    if (this.isEmpty() || !previousPath) return i18n('Home');
    if (this.isHomepageURL(previousPath.pathname)) return i18n('Home');
    if (this.isPopoverURL(this.history.location.pathname)) {
      const beforePrevious = this.peekAt(2);
      if (!beforePrevious || this.isHomepageURL(beforePrevious.pathname))
        return i18n('Home');
      return shortenedName
        ? i18n(this.getAppShortenedWidgetNameForPath(beforePrevious.pathname))
        : i18n(this.getAppNameForPath(beforePrevious.pathname));
    }
    return i18n(this.getPreviousWidgetName(shortenedName));
  }

  getPreviousWidget() {
    const previousURL = this.peekAt(1);
    if (!previousURL) return null;
    return this.getAppForPath(previousURL.pathname);
  }

  isURLMatchWithPartialURL(fullURL, partialURL) {
    return fullURL.match(`${partialURL}(?=$|[\\/?#])`) !== null;
  }

  isWidgetURL(url) {
    return this.widgets.find(
      (widget) =>
        this.isURLMatchWithPartialURL(url, widget.linkTo.pathname) &&
        !this.isPopoverURL(url)
    );
  }

  isHomepageURL(url) {
    return url === this.linkUrlFor('/') || url === this.linkUrlFor('');
  }

  isPopoverURL(url) {
    return (
      this.popoverWidgets.find((widget) => url === widget.linkTo.pathname) !== undefined
    );
  }

  getAppForPath(linkPath) {
    const navigationItem = [...this.widgets, ...this.popoverWidgets].find((widget) =>
      this.isURLMatchWithPartialURL(linkPath, widget.linkTo.pathname)
    );
    return navigationItem || null;
  }

  getAppNameForPath(linkPath) {
    const app = this.getAppForPath(linkPath);
    if (app) return app.nameMessage || '';
    return '';
  }

  getAppShortenedWidgetNameForPath(linkPath) {
    const app = this.getAppForPath(linkPath);
    if (app.nameMessage || app.shortenedName) {
      return app.shortenedName || app.nameMessage;
    }
    return '';
  }

  isNavigatingBetweenWidgets() {
    return (
      this.previousURL &&
      this.isWidgetURL(this.previousURL.pathname) &&
      this.isWidgetURL(this.history.location.pathname)
    );
  }

  isNavigatingInSameWidget() {
    if (!this.previousURL) return false;
    const widgetFrom = this.getAppNameForPath(this.previousURL.pathname);
    const widgetTo = this.getAppNameForPath(this.history.location.pathname);
    return widgetFrom === widgetTo;
  }

  /* Function that can be used from a popover that is actually part of a widget.
     When it does not make sense to go back to anything else than the widget, use this function.
     It supports both flows:
      - going from the widget to the popover
      - landing directly on the popover */
  goToWidgetFromPopover(widgetUrl, options) {
    let previousURL = this.peekAt(1);
    if (!previousURL) previousURL = this.linkUrlFor('');
    else previousURL = previousURL.pathname;
    if (this.isWidgetURL(previousURL)) {
      this.pop();
    }
    sendPageview(widgetUrl);
    this.replaceUrl(widgetUrl, options);
  }
}
